<template>
  <TableComp
    :name="$t('global.ftp-ssl')"
    :headers="headers"
    :status="status"
    :number-of-items="getNumberOfUsers()"
    :header-actions="getActions()"
    empty-image="ftp-users"
    :empty-actions="getEmptyActions()"
  >
    <template #content>
      <p>
        {{ getDescription() }} <a href="#" @click="moreInfo">{{ $t("global.learn-more") }}</a>
      </p>
      <CardListContent :list="getList()" />
    </template>
    <template #desktop>
      <tr v-for="(user, index) in users" v-bind:key="index">
        <td>{{ getUser(user) }}</td>
        <td class="text-center">
          {{ getHomeDirectory(user) }}
        </td>
        <td class="align-middle text-end actions">
          <div class="d-flex justify-content-center align-items-center">
            <ActionIcons :actions="getUserActions(user)" />
          </div>
        </td>
      </tr>
    </template>
    <template #mobile>
      <TableMobileRow v-for="(user, index) in users" v-bind:key="index">
        <template #header>
          <div class="d-flex align-items-center">
            <div class="flex-fill">
              <div class="d-flex align-items-center">
                <div class="flex-fill">{{ user.username }}</div>
                <div
                  v-if="status.isLoaded || status.isUpdating"
                  class="d-flex justify-content-center"
                >
                  <ActionIcons :actions="getUserActions(user)" />
                </div>
                <div v-else class="d-flex justify-content-center align-items-center">
                  <LoadingState :size="20" />
                </div>
              </div>
            </div>
          </div>
        </template>
        <template #content>
          <div class="row">
            <div class="col">{{ $t("global.username") }}:</div>
            <div class="col">{{ getUser(user) }}</div>
          </div>
          <div class="row">
            <div class="col">{{ $t("global.home-directory") }}:</div>
            <div class="col">{{ getHomeDirectory(user) }}</div>
          </div>
        </template>
      </TableMobileRow>
    </template>
  </TableComp>
</template>

<script>
import { computed, onMounted } from "@vue/composition-api";
import ActionIcons from "@/components/display/ActionIcons";
import CardListContent from "@/components/display/card-sub-components/CardListContent";
import CreateFtpUser from "@/components/smart/modals/CreateFtpUser";
import LoadingState from "@/components/display/LoadingState";
import NavLink from "@/classes/NavLink";
import RemoveFtpUser from "@/components/smart/modals/RemoveFtpUser";
import store from "@/store";
import TableComp from "@/components/display/table/TableComp";
import TableHeader from "@/classes/TableHeader";
import TableMobileRow from "@/components/display/table/TableMobileRow";
import UpdateFtpUser from "@/components/smart/modals/UpdateFtpUser";
import ViewPassword from "@/components/smart/modals/ViewPassword";

export default {
  name: "FtpSslTable",
  props: {
    resourceName: String,
    site: Object,
  },
  components: {
    ActionIcons,
    CardListContent,
    LoadingState,
    TableComp,
    TableMobileRow,
  },
  setup(props, { root }) {
    const PORT = 21;
    const users = computed(() => store.getters["ftp/getUsers"]);
    const status = computed(() => store.getters["ftp/getUsersStatus"]);
    const headers = [
      new TableHeader(root.$t("global.username")),
      new TableHeader(root.$t("global.home-directory"), "justify-content-center"),
      new TableHeader(),
    ];

    const getActions = () => [
      new NavLink(
        "Create FTP User",
        "add",
        openCreateForm,
        false,
        null,
        "epsilon",
        status.value.isLoading
      ),
    ];

    const getDescription = () => root.$t("global.ftp-ssl-description");

    const getEmptyActions = () => [
      new NavLink(
        "Create FTP User",
        null,
        openCreateForm,
        false,
        "primaryOutline",
        null,
        status.value.isLoading
      ),
    ];

    const getHomeDirectory = (user) => user.homeDir;

    const moreInfo = () =>
      window.open(
        "https://help.nexcess.net/77213-ftp/locating-your-ftps-credentials-for-nexcess-cloud",
        "_blank",
        "noopener, noreferrer"
      );

    const getList = () => [
      { label: root.$t("global.hostname"), content: props.site.tempDomain, canCopy: true },
      { label: root.$t("global.port"), content: PORT, canCopy: true },
    ];

    const getNumberOfUsers = () => users.value?.length || 0;
    const getTitle = () => root.$t("global.ftp-ssl");
    const getUser = (user) => user.user;

    const getUserActions = (user) => {
      let list = [];

      if (user.password) {
        list.push(
          new NavLink(
            root.$t("global.view-password"),
            "view",
            () => viewPassword(user),
            false,
            null,
            "epsilon",
            status.value.isUpdating
          )
        );
      }

      list.push(
        new NavLink(
          "Edit FTP User",
          "edit",
          () => openUpdateForm(user),
          false,
          null,
          "epsilon",
          status.value.isUpdating
        ),
        new NavLink(
          "Remove FTP User",
          "trash",
          () => openDeleteForm(user),
          false,
          null,
          "epsilon",
          status.value.isUpdating
        )
      );

      return list;
    };

    const openCreateForm = () =>
      store.dispatch("modals/open", {
        template: CreateFtpUser,
        data: { resourceName: props.resourceName, site: props.site },
      });

    const openDeleteForm = (user) =>
      store.dispatch("modals/open", {
        template: RemoveFtpUser,
        data: { ftpUsername: user.username, resourceName: props.resourceName, site: props.site },
      });

    const openUpdateForm = (user) =>
      store.dispatch("modals/open", {
        template: UpdateFtpUser,
        data: { ftpUser: user, resourceName: props.resourceName, site: props.site },
      });

    const viewPassword = (user) =>
      store.dispatch("modals/open", {
        template: ViewPassword,
        data: { password: user?.password },
      });

    onMounted(() => {
      store.dispatch("ftp/getUsers", props.site.id);
    });

    return {
      getActions,
      getDescription,
      getEmptyActions,
      getHomeDirectory,
      getList,
      getNumberOfUsers,
      getTitle,
      getUser,
      getUserActions,
      headers,
      moreInfo,
      status,
      users,
    };
  },
};
</script>
